import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Dropdown } from "primereact/dropdown";
import { Button } from "../../components/ui/Button";
import { Sidebar } from "primereact/sidebar";
import "../Media/css/mediaTable.css";

import { galleryRefetch, setData, setLastElement, setParams } from "../../redux/Slices/scrollSlice";
import { toggleDialog } from "../../redux/Slices/Global/globalSlice";
import { LINK_DEFAULT_FILTERS, SORT_BY_OPTION } from "../../config/media";
import { useGalleryContext } from "../Contexts/GalleryContext";
import { ProgressSpinner } from "primereact/progressspinner";
import FirebaseListener from "../Services/FirebaseMediaListener";
import ScrollContainer from "../SharedComponents/ScrollContainer";
import { useTitle } from "../../config/global";
import UploadPopup from "../Media/components/UploadPopup/UploadPopup";
import SearchBar from "../SharedComponents/Gallery/SearchBar";
import MediaCard from "../Media/components/MediaLayout/MediaCard";
import MediaFilter from "../SharedComponents/Gallery/MediaFilter";
import FloatingActionButton from "../SharedComponents/FloatingActionButton";
import Layout from "../Layout/Layout";

function LinksDataTable() {
  useTitle("Links");
  const { t } = useTranslation();

  const lastElementRef = useRef(null);
  const dispatch = useDispatch();

  const { layout, setLayout } = useGalleryContext();
  const { isFetching, data: media, params } = useSelector((state) => state.scroll);

  const [visible, setVisible] = useState(false);
  const [filters, setFilters] = useState(LINK_DEFAULT_FILTERS);

  useEffect(() => {
    if (lastElementRef.current) {
      dispatch(setLastElement(lastElementRef.current));
    }
  }, [dispatch, media?.length]);

  const getClasses = (index) => {
    return `w-98 ${
      layout === "list" ? `media_list ${index + 1 !== media.length ? "border-bottom border-gray" : ""} ` : "media_grid"
    } mx-2 py-2 media position-relative`;
  };

  const subNavbarProps = useMemo(() => {
    return {
      title: "Links",
    };
  }, []);

  const subNavbarContent = useMemo(() => {
    return (
      <div className="flex justify-end w-100">
        <div className="flex-grow-1 me-2">
          <SearchBar isDisabled={media?.length === 0} />
        </div>
        <div className="hidden md:block">
          <UploadPopup content="links" />
        </div>
      </div>
    );
  }, [media]);

  const onSortChange = (e) => {
    const sortOrder = e.value ?? e.target?.value;
    setFilters((prev) => ({ ...prev, sortOrder }));
    dispatch(setParams({ ...params, sortOrder }));
  };

  const handleApplyFilters = () => {
    dispatch(setData([]));
    dispatch(setParams({ ...params, ...filters }));
    setVisible(false);
  };

  return (
    <Layout subNavbarContent={subNavbarContent} subNavbarProps={subNavbarProps} padding={false}>
      <div className="dataview-demo px-4 pt-4">
        <div className="d-flex flex-column justify-content-start rounded-3 media_container links-media-container px-auto mx-1">
          <div className="col-12 px-3 py-2 py-sm-0 d-flex justify-content-between align-items-sm-center align-items-start btn_container">
            <div className="d-flex flex-sm-row flex-column mobile-breadcrumb-section" aria-label="Links toolbar breadcrumb section">
              <div className="d-md-none fw-semibold">{t('nav.links')}</div>
            </div>

            <div className="d-flex justify-content-end align-items-center align-items-start my-sm-3 w-[40%] mobile-actions-section">
              <div className="mobile-action-buttons d-md-none">
                <Dropdown
                  value={params?.sortOrder ?? "desc"}
                  options={SORT_BY_OPTION}
                  onChange={onSortChange}
                  optionLabel="label"
                  optionValue="value"
                  className="sort_by_dropdown touch-target"
                  aria-label={t('links_.sort_links')}
                  disabled={isFetching}
                />

                <Button
                  type="button"
                  icon="pi pi-filter-fill"
                  onClick={() => setVisible(true)}
                  className="p-button-text p-button-secondary p-0 touch-target"
                  aria-label={t('links_.open_filters')}
                  tooltip={t('common.filter')}
                  tooltipOptions={{ position: "bottom" }}
                />

                <Sidebar
                  visible={visible}
                  position="right"
                  className="p-sidebar-sm mobile-filter-sidebar"
                  onHide={() => setVisible(false)}
                >
                  <MediaFilter filters={filters} setFilters={setFilters} onApplyFilters={handleApplyFilters} />
                </Sidebar>
              </div>

              <div className="mobile-layout-toggle" role="group" aria-label={t('links_.view_mode')}>
                <Button
                  type="button"
                  icon="pi pi-microsoft"
                  onClick={() => setLayout("grid")}
                  className={`p-button-text p-button-secondary p-0 fs-5 touch-target ${layout === "grid" ? "" : "text-[#CCCCCE]"}`}
                  aria-label={t('links_.grid_view')}
                  tooltip={t('links_.grid_view')}
                  tooltipOptions={{ position: "bottom" }}
                />
                <Button
                  type="button"
                  icon="pi pi-list"
                  onClick={() => setLayout("list")}
                  className={`p-button-text p-button-secondary p-0 fs-4 touch-target ${layout === "list" ? "" : "text-[#CCCCCE]"}`}
                  aria-label={t('links_.list_view')}
                  tooltip={t('links_.list_view')}
                  tooltipOptions={{ position: "bottom" }}
                />
              </div>
            </div>
          </div>

          <ScrollContainer path={`/folder/media`} showLoading={false}>
            <div className="col-12 d-flex uppercase font-bold text-[#ADADAD] text-[12px] border-b-2 pb-2 mt-2 tracking-widest">
              <div className="col-4 mobile-hide">
                {isFetching ? (
                  t('table.loading')
                ) : (
                  <Dropdown
                    value={params?.sortOrder ?? "desc"}
                    options={SORT_BY_OPTION}
                    onChange={onSortChange}
                    optionLabel="label"
                    optionValue="value"
                    className="sort_by_dropdown touch-target"
                    aria-label={t('links_.sort_links')}
                  />
                )}
              </div>
              {layout === "list" ? (
                <>
                  <div className="col-md-1 col-2 px-0 text-center d-none d-sm-block mobile-hide">{t('links_.duration')}</div>
                  <div className="col-2 px-0 text-center d-none d-sm-block mobile-hide">{t('links_.kind')}</div>
                  <div className="col-md-2 col-2 px-0 text-center d-none d-md-block">{t('links_.size')}</div>
                  <div className="col-2 px-0 text-center d-md-none">{t('links_.duration')}</div>
                  <div className="col-md-2 col-2 px-0 text-center d-none d-xl-block mobile-hide">{t('links_.created_date')}</div>
                  <div className="col-3"> </div>
                </>
              ) : (
                <></>
              )}
            </div>
            <div className={`row justify-content-center pb-3 ${layout === "list" ? "h-[82%]" : "h-[91%]"}`}>
              {!isFetching && media?.length === 0 ? (
                <h5 className="py-5 text-center w-100">{t('links_.no_media_yet')}</h5>
              ) : (
                <div className="cards_container object-fit row align-content-start justify-content-start align-items-start mx-auto scroll_div overflow-x-hidden px-2 pp-fab-safe-bottom">
                  {media?.map((item, index) => {
                    return (
                      <div key={item.id} className={getClasses(index)} ref={index === media.length - 1 ? lastElementRef : null}>
                        <MediaCard
                          item={item}
                          layout={layout}
                          forceMobileActions
                          showCategoryIcon
                          showMobileDuration
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {isFetching && media.length === 0 ? (
                <div className="w-full p-4">
                  <ProgressSpinner
                    className="h-[50px] w-[50px] flex justify-center items-center"
                    strokeWidth="3"
                    fill="var(--surface-ground)"
                    animationDuration="30s"
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
          </ScrollContainer>
        </div>
      </div>

      <FirebaseListener path={"/Companies/" + localStorage.getItem("company_id") + "/actions"} getData={() => dispatch(galleryRefetch())} />

      <FloatingActionButton
        icon="fas fa-plus"
        label={t('links_.add_link')}
        onClick={() => dispatch(toggleDialog("uploadMedia"))}
      />
    </Layout>
  );
}

export default LinksDataTable;
